import type { AwardRecordFacts, AwardView, RoleKey } from '@myrewards/contracts';
import {
  daysBetween,
  formatAuditTimestamp,
  formatDate,
  formatShortDate,
  organizationDate,
  scoped,
  type ScopedLabel,
  type StatusTone,
} from '../../ui';
import { STAGE_WORDS, statusOf } from '../present';

// The award record's hero, Values used and trail in words (G1, APP-069). Pure, so each rule is
// tested on its own.

type Line = AwardView['calculation']['lines'][number];

/** The hero's way back (the shell's HeroBack): what shows, and the link's full name. */
export interface BackLink {
  href: string;
  /** What shows in Full: "In progress". */
  label: string;
  /** The link's name, which includes the label: "Back to In progress". */
  name: string;
}

const backTo = (label: string, href: string): BackLink => ({
  href,
  label,
  name: `Back to ${label}`,
});

/** A page that opens the record can say where it came from: `navigate(to, { state: { back } })`. */
export function backFromState(state: unknown): BackLink | null {
  if (typeof state !== 'object' || state === null || !('back' in state)) return null;
  const back = state.back;
  if (typeof back !== 'object' || back === null) return null;
  const { label, href } = back as Record<string, unknown>;
  // Same-origin app paths only (not "//elsewhere" or "https://…"): never somewhere else.
  return typeof label === 'string' &&
    label.trim() !== '' &&
    typeof href === 'string' &&
    /^\/(?![/\\])/.test(href)
    ? backTo(label.trim(), href)
    : null;
}

/**
 * Where "back" goes: where the record was opened from when that page said so; else the list the
 * person works this award from: their own drafts and submissions, the Stock Admin team's
 * "Everything in progress", or Home. A Spot award was never drafted or sent by whoever it's
 * entered under (its approver, APP-108), and never sits in the Stock Admin queue: its approver
 * goes back to Spot nominations, where Spot decisions are made, and anyone else to Home (APP-110).
 */
export function backLink(
  state: unknown,
  ctx: { mine: boolean; roles: readonly RoleKey[]; spot?: boolean },
): BackLink {
  const from = backFromState(state);
  if (from !== null) return from;
  if (ctx.spot === true)
    return ctx.mine ? backTo('Spot nominations', '/recognize/spot') : backTo('Home', '/');
  if (ctx.mine) return backTo('My drafts & submitted', '/awards');
  if (ctx.roles.includes('stock_admin')) return backTo('In progress', '/manage/queue/in-progress');
  return backTo('Home', '/');
}

/** The state beside the title: its stage, with the choice's due date when there is one. */
export function heroPill(
  view: Pick<AwardView, 'stage' | 'ended'>,
  electionDue: string | null,
): { label: string; tone: StatusTone } {
  const status = statusOf(view);
  if (view.ended === null && view.stage === 'employee_choice' && electionDue !== null)
    return {
      label: `${STAGE_WORDS.employee_choice.label} · due ${formatShortDate(electionDue)}`,
      tone: status.tone,
    };
  return status;
}

/**
 * "Meredith Okonkwo (Stock Admin) for Graham Whitlock": the role sits after the person it
 * describes, as the Decisions line has it (R126), never after whom it was for.
 */
const stepText = (step: AwardRecordFacts['enteredBy']) =>
  `${step.name}${step.roles === null ? '' : ` (${step.roles})`}${step.onBehalfOf === null ? '' : ` for ${step.onBehalfOf}`}`;

/**
 * A Spot award's who and when (QA-054): "nominated by Avery Stone on Oct 1, 2026 · approved by
 * Tessa Lindgren on Oct 14, 2026", or "a colleague" for a reader who may not see who nominated.
 * The award is made when it's approved, so until the record's words load, its approver is the
 * person who entered it.
 */
function spotTail(view: AwardView, facts: AwardRecordFacts | undefined): string {
  const nominated = facts?.nominatedBy ?? null;
  const approved = facts?.approvedBy ?? null;
  if (nominated === null)
    return `approved by ${view.createdBy.name} on ${formatDate(view.createdAt)}`;
  return [
    `nominated by ${nominated.name ?? 'a colleague'} on ${formatDate(nominated.at)}`,
    ...(approved === null
      ? []
      : [`approved by ${stepText(approved)} on ${formatDate(approved.at)}`]),
  ].join(' · ');
}

/**
 * One line under the title. One person: "Jordan Ellis · EE# 10482 · Maritime HydroVision ·
 * entered by Imani Rowe (Sector Lead) on Sep 22, 2026". More: the title and headcount instead.
 * A Spot award says who nominated and who approved it instead.
 */
export function heroSub(view: AwardView, facts: AwardRecordFacts | undefined): string {
  const lines = view.calculation.lines;
  const one = lines.length === 1 ? lines[0] : undefined;
  const person =
    one === undefined ? undefined : facts?.people.find((p) => p.personId === one.person.id);
  const who: string[] =
    one === undefined
      ? [...(view.title === null ? [] : [view.title]), `${String(lines.length)} people`]
      : [
          one.person.name,
          ...(person?.employeeNumber == null ? [] : [`EE# ${person.employeeNumber}`]),
          ...(person?.sector == null ? [] : [person.sector]),
        ];
  if (view.type === 'SPOT') return [...who, spotTail(view, facts)].join(' · ');
  const entered = facts?.enteredBy;
  const by =
    entered === undefined
      ? view.onBehalfOf === null
        ? view.createdBy.name
        : `${view.createdBy.name} for ${view.onBehalfOf.name}`
      : stepText(entered);
  const when = formatDate(view.createdAt);
  const tail =
    view.submittedAt === null
      ? `draft started by ${by} on ${when} · not sent yet`
      : `entered by ${by} on ${when}`;
  return [...who, tail].join(' · ');
}

/**
 * The second hero number while people are choosing: "Jordan Ellis’s choice · due" Oct 21, with
 * the days left; else where the award is.
 */
export function choiceStat(
  view: AwardView,
  electionDue: string | null,
  choosing: readonly string[],
  now: number = Date.now(),
): { label: ScopedLabel; value: string; meta: string; tone: 'gold' } | null {
  if (view.ended !== null || view.stage !== 'employee_choice' || electionDue === null) return null;
  // Whole days in the organization's calendar: due Oct 21, today Oct 14, is 7 days left.
  const days = daysBetween(
    organizationDate(new Date(now).toISOString()),
    organizationDate(electionDue),
  );
  const left =
    days < 0
      ? 'past the deadline'
      : days === 0
        ? 'due today'
        : `${String(days)} ${days === 1 ? 'day' : 'days'} left`;
  const [only] = choosing;
  return {
    label:
      choosing.length === 1 && only !== undefined
        ? scoped(`${only}’s choice`, 'due')
        : scoped(view.displayId ?? 'This award', 'choices due'),
    value: formatShortDate(electionDue),
    meta:
      choosing.length <= 1
        ? `${left} · not made yet`
        : `${left} · ${String(choosing.length)} still to choose`,
    tone: 'gold',
  };
}

const costTypeWords = (c: 'OH' | 'GA') => (c === 'GA' ? 'G&A' : 'OH');

/**
 * The charge code on Values used: one code for the award ("0535-006 (OH)", with its org and
 * project), or "By person" listing the codes when people are charged differently.
 */
export function chargeCode(lines: readonly Line[]): { value: string; sub: string } {
  const coded = lines.flatMap((l) => (l.coding === null ? [] : [l.coding]));
  if (coded.length === 0)
    return { value: 'Not set yet', sub: 'Worked out from each person’s org when it’s checked' };
  const key = (c: (typeof coded)[number]) =>
    `${c.account}|${c.costType}|${c.org}|${c.project ?? ''}`;
  const distinct = [...new Map(coded.map((c) => [key(c), c])).values()];
  const [first] = distinct;
  if (distinct.length === 1 && first !== undefined)
    return {
      value: `${first.account} (${costTypeWords(first.costType)})`,
      sub: `Org ${first.org} · ${first.project === null ? 'no project' : `project ${first.project}`}`,
    };
  const accounts = [...new Set(distinct.map((c) => `${c.account} (${costTypeWords(c.costType)})`))];
  return {
    value: 'By person',
    sub: `${accounts.join(' · ')} · each person’s code is in People`,
  };
}

/** The trail's time in two lines, as board G1 shows it: "Oct 15, 2026" over "15:46:11 UTC". */
export function trailWhen(iso: string): { date: string; time: string } {
  const [date = '', time = ''] = formatAuditTimestamp(iso).split(' · ');
  return { date, time };
}
